/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Inline, VerticalDivider, Heading, Text } from 'sarsaparilla';
import { FavoriteHeart } from 'shared-ui/favorites';

import { SitePassPageHeaderPlaceholder } from './SitePassPageHeaderPlaceholder';

/**
 * heading: top level heading of the page
 * parentLinkText: anchor tag text (used in combo with parentLinkUrl)
 * parentLinkUrl: provides url to parentLinkText
 * isParentLinkSameService: "true" removes external link icon
 */

/**
 * NOTE: This component is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */
export default function SitePassPageHeader({
    isLoading = false,
    heading,
    facilityName,
    isPartOfParent = false,
    parentLinkText,
    parentLinkUrl,
    isParentLinkSameService = false,
    favoriteFacilityId,
    favoriteEntityId,
    favoriteEntityType,
    favoriteParentId,
    favoriteParentType,
    className,
    isInWidget = false,
}) {
    if (isLoading) {
        return <SitePassPageHeaderPlaceholder />;
    }
    const locationChain = [];
    const hasParentLink = parentLinkText && parentLinkUrl;
    // FavoriteHeart component uses redux and widget does not support redux. So, we need to hide this component
    // UPDATE Sept 2026: This is no longer correct as there are no longer any standalone widget applications.
    // However people seeing this content in an iframe probably don't need to be saving favorites anyway.
    const hasFavoriteButton = (favoriteFacilityId || favoriteEntityId) && !isInWidget;
    const hasLocationChain = hasParentLink || hasFavoriteButton;

    if (hasParentLink && !isPartOfParent) {
        if (isParentLinkSameService) {
            locationChain.push(
                <Link to={parentLinkUrl} key="link" translate="no">
                    {parentLinkText}
                </Link>
            );
        } else {
            locationChain.push(
                <a
                    href={parentLinkUrl}
                    key="link"
                    rel="noreferrer"
                    target="_blank"
                    translate="no"
                >
                    {parentLinkText}
                </a>
            );
        }
    }
    if (!isInWidget && hasParentLink && isPartOfParent) {
        if (isParentLinkSameService) {
            locationChain.push(
                <span key="partOf">
                    Part of &nbsp;
                    <Link to={parentLinkUrl} key="link" translate="no">
                        {parentLinkText}
                    </Link>
                </span>
            );
        } else {
            locationChain.push(
                <span key="partOf">
                    Part of{' '}
                    <a
                        href={parentLinkUrl}
                        key="link"
                        rel="noreferrer"
                        target="_blank"
                        translate="no"
                    >
                        {parentLinkText}
                    </a>
                </span>
            );
        }
    }
    if (locationChain?.length && hasFavoriteButton) {
        locationChain.push(<VerticalDivider key="mobile-favorite-divider" />);
    }

    if (hasFavoriteButton) {
        locationChain.push(
            <FavoriteHeart
                id={favoriteFacilityId}
                entityId={favoriteEntityId}
                entityType={favoriteEntityType}
                parentId={favoriteParentId}
                parentType={favoriteParentType}
                showFavoriteText
                key="favorite-button"
            />
        );
    }

    return (
        <div data-component="SitePassPageHeader" className={className}>
            <div>
                <Heading headingLevel={1} appearance="title" translate="no">
                    {heading}
                </Heading>
                <Text color="gray" size="lg">
                    {facilityName}
                </Text>
                {hasLocationChain && (
                    <div className="location-chain mt-1 mb-1">
                        <Inline space={['xs', 'sm', 'md']}>{locationChain}</Inline>
                    </div>
                )}
            </div>
        </div>
    );
}

SitePassPageHeader.propTypes = {
    heading: PropTypes.string,
    facilityName: PropTypes.string,
    parentLinkText: PropTypes.string,
    parentLinkUrl: PropTypes.string,
    isPartOfParent: PropTypes.bool,
    favoriteFacilityId: PropTypes.string,
    favoriteEntityId: PropTypes.string,
    favoriteEntityType: PropTypes.string,
    favoriteParentId: PropTypes.string,
    favoriteParentType: PropTypes.string,
    className: PropTypes.string,
    isLoading: PropTypes.bool,
    isParentLinkSameService: PropTypes.bool,
    isInWidget: PropTypes.bool,
};
